/**
 * VerifyEmail.jsx — Handles the email verification link.
 * URL: /verify-email?token=...
 *
 * Fixes:
 *  - Shows helpful message when token already used (already verified)
 *  - Resend button works WITHOUT being logged in (user provides email)
 *  - Distinguishes expired vs invalid tokens clearly
 */

import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert, Box, Button, CircularProgress, Paper, TextField, Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import { apiClient } from '../utils/auth';

export default function VerifyEmail() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [state, setState] = useState('loading'); // loading | success | error | expired | used
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (!token) {
      setState('error');
      setMessage('No verification token found in the link. Please check your email and try again.');
      return;
    }

    apiClient.post('/api/v1/auth/email/verify/', { token })
      .then(({ data }) => {
        // Backend returns HTTP 200 in all non-error cases.
        // Check data.code first — INVALID_OR_USED_TOKEN means token was already cleared.
        if (data.code === 'INVALID_OR_USED_TOKEN') {
          setState('used');
          setMessage(data.message || 'This verification link has already been used. If your email is verified, you can sign in directly.');
        } else if (data.already_verified) {
          setState('used');
          setMessage('Your email is already verified! You can sign in now.');
        } else {
          setState('success');
          setMessage(data.message || 'Email verified successfully.');
        }
      })
      .catch((err) => {
        const code = err.response?.data?.error?.code;
        if (code === 'TOKEN_EXPIRED') {
          setState('expired');
          setMessage('This verification link has expired (links are valid for 24 hours). Please request a new one.');
        } else if (code === 'ALREADY_VERIFIED' || code === 'INVALID_OR_USED_TOKEN') {
          setState('used');
          setMessage('This verification link has already been used. If your email is verified, you can sign in directly.');
        } else {
          setState('error');
          setMessage(err.response?.data?.error?.message || 'Verification failed. The link may be invalid.');
        }
      });
  }, [token]);

  return (
    <Box sx={{ minHeight: '80vh', display: 'flex', alignItems: 'center', justifyContent: 'center', px: 2 }}>
      <Paper
        elevation={3}
        sx={{
          maxWidth: 480, width: '100%', p: 4, borderRadius: 3,
          textAlign: 'center',
          background: 'linear-gradient(135deg, rgba(13,21,38,0.95) 0%, rgba(30,41,59,0.95) 100%)',
          border: '1px solid rgba(129,140,248,0.15)',
        }}
      >
        {state === 'loading' && (
          <>
            <CircularProgress sx={{ mb: 2 }} />
            <Typography color="text.secondary">Verifying your email…</Typography>
          </>
        )}

        {state === 'success' && (
          <>
            <CheckCircleIcon color="success" sx={{ fontSize: 60, mb: 2 }} />
            <Typography variant="h5" fontWeight={700} gutterBottom>Email Verified! 🎉</Typography>
            <Typography color="text.secondary" sx={{ mb: 3 }}>{message}</Typography>
            <Button variant="contained" fullWidth onClick={() => navigate('/login')} id="btn-verify-success-signin">
              Sign In Now
            </Button>
          </>
        )}

        {state === 'used' && (
          <>
            <MarkEmailReadIcon sx={{ fontSize: 60, mb: 2, color: '#818cf8' }} />
            <Typography variant="h5" fontWeight={700} gutterBottom>Already Verified</Typography>
            <Typography color="text.secondary" sx={{ mb: 3 }}>{message}</Typography>
            <Button variant="contained" fullWidth onClick={() => navigate('/login')} id="btn-already-verified-signin">
              Sign In
            </Button>
            <Button variant="text" fullWidth onClick={() => navigate('/register')} sx={{ mt: 1 }}>
              Register a new account
            </Button>
          </>
        )}

        {state === 'expired' && (
          <>
            <HourglassTopIcon color="warning" sx={{ fontSize: 60, mb: 2 }} />
            <Typography variant="h5" fontWeight={700} gutterBottom>Link Expired</Typography>
            <Typography color="text.secondary" sx={{ mb: 3 }}>{message}</Typography>
            <ResendVerificationByEmail />
            <Button variant="outlined" fullWidth sx={{ mt: 1 }} onClick={() => navigate('/login')} id="btn-expired-back">
              Back to Login
            </Button>
          </>
        )}

        {state === 'error' && (
          <>
            <ErrorIcon color="error" sx={{ fontSize: 60, mb: 2 }} />
            <Typography variant="h5" fontWeight={700} gutterBottom>Verification Failed</Typography>
            <Typography color="text.secondary" sx={{ mb: 3 }}>{message}</Typography>
            <Alert severity="info" sx={{ mb: 2, textAlign: 'left' }}>
              <strong>Already registered?</strong> If you previously verified your email, you can sign in directly.
              If not, enter your email below to get a new link.
            </Alert>
            <ResendVerificationByEmail />
            <Button variant="outlined" fullWidth sx={{ mt: 1 }} onClick={() => navigate('/login')} id="btn-error-back">
              Back to Login
            </Button>
          </>
        )}
      </Paper>
    </Box>
  );
}

/**
 * Resend verification link WITHOUT needing to be logged in.
 * User just provides their email address.
 */
function ResendVerificationByEmail() {
  const [email, setEmail]   = useState('');
  const [sent, setSent]     = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError]   = useState('');

  const handleResend = async () => {
    const trimmed = email.trim().toLowerCase();
    if (!trimmed || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setError('Please enter a valid email address.');
      return;
    }
    setLoading(true); setError('');
    try {
      const { data } = await apiClient.post('/api/v1/auth/email/resend-by-email/', { email: trimmed });
      if (data.already_verified) {
        setError('');
        setSent(true);
        // override message shown
        setTimeout(() => {}, 0);
        setSent('already');
      } else {
        setSent(true);
      }
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Failed to send. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (sent === 'already') {
    return (
      <Alert severity="success" sx={{ mb: 1 }}>
        This email is already verified. Please <strong>sign in</strong> directly.
      </Alert>
    );
  }

  if (sent) {
    return (
      <Alert severity="success" sx={{ mb: 1 }}>
        ✓ New verification email sent to <strong>{email}</strong>. Please check your inbox (and spam folder).
      </Alert>
    );
  }

  return (
    <Box sx={{ mb: 1 }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Enter your registered email to receive a new verification link:
      </Typography>
      <TextField
        label="Email address"
        type="email"
        value={email}
        onChange={(e) => { setEmail(e.target.value); setError(''); }}
        fullWidth
        size="small"
        sx={{ mb: 1 }}
        error={!!error}
        helperText={error}
        id="input-resend-email"
      />
      <Button
        variant="contained"
        fullWidth
        onClick={handleResend}
        disabled={loading}
        id="btn-resend-verification"
        sx={{ mb: 1 }}
      >
        {loading ? <CircularProgress size={20} color="inherit" /> : 'Send New Verification Link'}
      </Button>
    </Box>
  );
}
